import { IBasicRightResults } from "UI/UI/Rights/BasicRightsResults";
import { ActionType } from "../../../../UI.Controls/Actions/IAction";
import { ILogicalComponent } from "../../../LogicalTree/ILogicalComponent";
import { RightsEvaluationOverrides } from "../../../Rights/RightsEvaluationOverrides";
import { StandardAction } from "../Actions/StandardAction";
import { IFrameWithRights } from "../IFrame";
import {
    EditStyleType,
    IEditStyleMasterableFrame,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
    IMultiRecordsComponent,
} from "./FrameInterfaces";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { IDetailFrame } from "./IDetailMasterableFrame";
import { IEditableFrame } from "./IEditableFrame";

export class ListFrameRightsEvaluationOverrides extends RightsEvaluationOverrides {
    protected override _initializeOverrides(): void {
        super._initializeOverrides();
    }

    protected override readonly __frame: IListFrameForOverrides;

    protected override get _frame(): IListFrameForOverrides {
        return this.__frame;
    }

    protected override _standardActionsInsert(controlWrapper: ILogicalComponent, _basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): boolean | null {
        const standardAction = controlWrapper.control as StandardAction;
        if (standardAction && standardAction.actionType === ActionType.standardNewRecord) {
            return (this._frame.masterFrame == null || this._frame.masterFrame.actualObject != null) // Nemá máster, nebo je na masteru vybraný objekt.
                && (this._frame.detail || !FrameSharedMethods.isFormNameImplicit(this._frame.detailForm)) // Frame má detail, nebo nemá nastavený detailForm jako IMPLICIT
                && this._frame.actualObjectStandardRights.canNew
                && this._frame.interactive
                && !this._getDisabledFromParent(allBasicRightsResult);
        }
        return null;
    }

    protected override async _standardActionsEnabled(controlWrapper: ILogicalComponent, basicRightResult?: boolean | null, allBasicRightsResult?: IBasicRightResults): Promise<boolean | null> {
        const superResult = super._standardActionsEnabled(controlWrapper, basicRightResult, allBasicRightsResult);
        this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for enabled right for StandardAction. OwnerFrame enabled is "${this._frame.enabled}".`, 10); // ` TODO-rites: remove !!

        const standardAction = controlWrapper.control as StandardAction;

        if (standardAction) {
            switch (standardAction.actionType) {
                case ActionType.standardSelectRecord:
                    if (this._frame instanceof IMasterableFrame && this._frame.ownerForm && this._frame.ownerForm.enableLovSelect) {
                        return !this._frame.isEditing/* && this._frame.isActive*/; //TODO-rites: isActive? To asi ne
                    } else {
                        return false;
                    }
                case ActionType.standardGraph:
                    return this._frame.masterFrame && !this._frame.masterObject ? false : this._frame.isActive;
                case ActionType.standardRefreshList:
                case ActionType.standardRefreshRecord:
                    if (this._frame.masterFrame && !this._frame.masterObject && this._frame instanceof IMasterableFrameWithDataGrid) {
                        return this._frame.currentDataGrid ? this._frame.currentDataGrid.dataRows.length && this._frame.interactive && !this._frame.isEditing : null;
                    } else {
                        return this._frame.interactive && !this._frame.isEditing;
                    }
                case ActionType.standardNewRecord:
                    // Disabluje tlačítko pokud je tento seznam podřízený (tzn. obsahuje masterFrame) nebo tento seznam neobsahuje detail frame a zároveň je atribut detailForm nastavený na "IMPLICIT".
                    // TODO-rites zkontrolovat, jestli stejné podmínky neplatí i pro ActionType.standardCopyRecord
                    if ((this._frame.masterFrame || !this._frame.detail) && FrameSharedMethods.isFormNameImplicit(this._frame.detailForm)) {
                        return false;
                    } else if (this._frame.masterFrame && !this._frame.masterObject) {
                        return this._frame.actualObject
                            && (!this._frame.isEditing || this._isMultiEditingMasterableFrame(this._frame.masterFrame));
                    } else {
                        return this._frame.actualObjectStandardRights.canNew && this._frame.interactive && !this._getDisabledFromParent(allBasicRightsResult) && (!this._frame.isEditing || this._isMultiEditingMasterableFrame(this._frame));
                    }
                case ActionType.standardDeselectAll:
                    return this._frame.isMultiSelectingMode && !this._frame.isChanged;
                case ActionType.standardRowActions:
                    if (!this._frame.enabled) {
                        return true;
                    }
            }
        }

        return superResult;
    }

    protected override async _standardActionsVisible(controlWrapper: ILogicalComponent): Promise<boolean | null> {
        this._debugPrintMethod(`[RightsEvaluationOverrides] Executing right override function for visible right for StandardAction. OwnerFrame enabled is "${this._frame.enabled}".`, 10); // ` TODO-rites: remove !!

        const standardAction = controlWrapper.control as StandardAction;

        if (standardAction) {
            switch (standardAction.actionType) {
                case ActionType.standardSelectRecord:
                    return this._frame.isLovFrame;
                case ActionType.standardEditRecord:
                    if (this._frame.isEditing) return false;
                    if (this._frame.isEditable || !!this._frame.detailForm || !!this._frame.detail) return true;
                    if (this._frame instanceof IMultiRecordsComponent) {
                        if (this._frame.isMultiSelectingMode) return true;
                    }

                    return false;
                case ActionType.standardCopyRecord:
                case ActionType.standardDeleteRecord:
                    return (this._frame.isEditable || !!this._frame.detailForm || !!this._frame.detail) && !this._frame.isEditing;
                case ActionType.standardSelectAll:
                case ActionType.standardDeselectAll:
                    return this._frame.isMultiSelectingMode;
                case ActionType.standardNewRecord:
                    if (!this._frame.isEditable && !this._frame.detailForm && !this._frame.detail) return false;
                    return ((this._frame.editStyle === EditStyleType.esMulti) ? true : !this._frame.isEditing) && (this._frame instanceof IMultiRecordsComponent && !this._frame.isMultiSelectingMode);
            }
        }

        return await super._standardActionsVisible(controlWrapper);
    }

    /**
     * Pokud je `frame` instancí {@link IEditStyleMasterableFrame} a zároveň je v módu multieditace vrací `true`, jinak `false`.
     * @param frame
     * @private
     */
    protected _isMultiEditingMasterableFrame(frame: IFrameWithRights): boolean {
        return frame instanceof IEditStyleMasterableFrame && frame.editStyle === EditStyleType.esMulti;
    }
}

export interface IListFrameForOverrides extends IEditableFrame {
    editStyle: EditStyleType;
    isMultiSelectingMode: boolean;
    detailForm: string;
    detail: IDetailFrame;
    isLovFrame?: boolean;
}
